import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { LabelKeyToProofType, EOperationProofType, ProofTypeLabelKeys } from 'src/app/core/_enums/proofTypes.code';
import { OperationProof, OperationProofWithFile } from 'src/app/core/_models/operationProof';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dialog-justificative',
  templateUrl: './dialog-justificative.component.html',
  styleUrls: ['./dialog-justificative.component.scss']
})
export class DialogJustificativeComponent implements OnInit {
  @ViewChild('tabGroup') tabGroup!: MatTabGroup;

  form: FormGroup;
  selectedJustificativeControl = new FormControl(null);
  fileName: string | null = null;
  file: File | null = null;
  fileToUpload: File | null = null;

  justificationOptions: string[] = Object.values(ProofTypeLabelKeys);
  fileUrl: SafeUrl | null = null;
  isDragging = false;

  uniqueProofs: OperationProof[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogJustificativeComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      operationProofs: OperationProof[];
      resourceName?: string;
      resourceType?: 'input' | 'output' | 'vehicle';
    },
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      justificationTypes: [[], Validators.required],
      comment: ['']
    });
  }

  ngOnInit(): void {
    if (this.data.resourceType) {
      this.justificationOptions = this.getFilteredJustificationOptions(this.data.resourceType);
    }

    if (this.data.operationProofs) {
      const seen = new Map<string, boolean>();
      this.uniqueProofs = this.data.operationProofs.filter((j) => {
        const key = `${j.fileName}|${j.comment}`;
        if (seen.has(key)) {
          return false;
        } else {
          seen.set(key, true);
          return true;
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.fileUrl) {
      URL.revokeObjectURL(this.fileUrl as string);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  private getFilteredJustificationOptions(resourceType: 'input' | 'output' | 'vehicle'): string[] {
    switch (resourceType) {
      case 'input':
      case 'output':
        return [ProofTypeLabelKeys[EOperationProofType.ProductType], ProofTypeLabelKeys[EOperationProofType.ProductQuantity], ProofTypeLabelKeys[EOperationProofType.TransportType], ProofTypeLabelKeys[EOperationProofType.TransportDistance]];
      case 'vehicle':
        return [ProofTypeLabelKeys[EOperationProofType.VehicleType], ProofTypeLabelKeys[EOperationProofType.EnergyType], ProofTypeLabelKeys[EOperationProofType.VehicleDays], ProofTypeLabelKeys[EOperationProofType.VehicleCount]];
      default:
        return Object.values(ProofTypeLabelKeys);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFile(input.files[0]);
    }
    input.value = '';
  }

  private processFile(file: File): void {
    this.file = file;
    this.fileToUpload = file;
    this.fileName = this.file.name;
    this.fileUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.file));
  }

  removeFile(): void {
    if (this.fileUrl) {
      URL.revokeObjectURL(this.fileUrl as string);
    }
    this.file = null;
    this.fileToUpload = null;
    this.fileName = null;
    this.fileUrl = null;
  }

  onSave(): void {
    if (this.tabGroup.selectedIndex === 0) {
      if (this.form.invalid) {
        return;
      }

      const justificationTypes = this.form.value.justificationTypes as string[];
      const proovedTypes = justificationTypes.map((displayName) => LabelKeyToProofType[displayName]).filter((type) => type !== undefined);

      const result: Partial<OperationProofWithFile> = {
        fileName: this.fileName || '',
        proovedTypes: proovedTypes,
        comment: this.form.value.comment,
        createDate: new Date()
      };

      if (this.fileToUpload) {
        (result as any).fileToUpload = this.fileToUpload;
      }

      this.dialogRef.close(result);
    } else {
      const selected = this.selectedJustificativeControl.value;
      if (selected) {
        this.dialogRef.close(selected);
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isSaveDisabled(): boolean {
    if (this.tabGroup && this.tabGroup.selectedIndex === 0) {
      return this.form.invalid;
    }
    if (this.tabGroup && this.tabGroup.selectedIndex === 1) {
      return !this.selectedJustificativeControl.value;
    }
    return true;
  }

  getProofTypesDisplay(proof: OperationProof): string {
    if (!proof.proovedTypes || proof.proovedTypes.length === 0) {
      return '';
    }

    return proof.proovedTypes.map((type) => (ProofTypeLabelKeys[type] ? this.translate.instant(ProofTypeLabelKeys[type]) : type)).join(', ');
  }
}
